import { type ReactNode, useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { HardHat, Loader2 } from "lucide-react";
import { ClientOfferView } from "@/components/ClientOfferView";
import {
  buildGetOfferShareFunctionUrl,
  decodePublicTokenRouteParam,
  type OfferSharePayload,
} from "@/lib/offerShares";
import {
  DEFAULT_TEMPLATE_ID,
  OFFER_TEMPLATES,
  type OfferTemplateId,
} from "@/types/offerTemplates";
import type { TemplateType } from "@/components/offer/offerDocumentTypes";

/** Strona marketingowa — można nadpisać w `.env` (Vite). */
const LANDING_PAGE_HREF =
  (import.meta.env.VITE_LANDING_PAGE_URL as string | undefined)?.trim() || "/";

const VALID_TEMPLATE_IDS = new Set<OfferTemplateId>(OFFER_TEMPLATES.map((t) => t.id));

function resolvePublicOfferTemplateId(raw: unknown): OfferTemplateId {
  if (typeof raw !== "string" || !VALID_TEMPLATE_IDS.has(raw as OfferTemplateId)) {
    return DEFAULT_TEMPLATE_ID;
  }
  return raw as OfferTemplateId;
}

function resolveTemplateType(raw: unknown): TemplateType {
  if (raw === "quick" || raw === "explained" || raw === "premium") return raw;
  return "quick";
}

type PublicOfferState =
  | { kind: "loading" }
  | { kind: "notFound" }
  | { kind: "error" }
  | {
      kind: "loaded";
      payload: OfferSharePayload;
      acceptedAt: string | null;
      acceptedByName: string | null;
    };

function parsePayloadJson(raw: unknown): OfferSharePayload | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  if (o.schemaVersion !== 1) return null;
  if (!Array.isArray(o.lines)) return null;
  if (!o.wizard || typeof o.wizard !== "object") return null;
  if (typeof o.vatMode !== "string") return null;
  if (!("draftLabel" in o)) return null;
  const clientOffer = o.clientOffer;
  if (!clientOffer || typeof clientOffer !== "object") return null;
  const co = clientOffer as Record<string, unknown>;
  if (typeof co.offerDate !== "string") return null;
  return {
    ...(raw as OfferSharePayload),
    templateType: resolveTemplateType(o.templateType),
  };
}

function UnavailableShell({ children }: { children: ReactNode }) {
  return (
    <div
      className="mx-auto flex min-h-[100dvh] max-w-[640px] flex-col items-center justify-center gap-6 px-6 text-center"
      style={{
        background: "var(--color-bg-page)",
        color: "var(--color-text-primary)",
        paddingTop: "max(1.5rem, env(safe-area-inset-top))",
        paddingRight: "max(1.5rem, env(safe-area-inset-right))",
        paddingBottom: "max(1.5rem, env(safe-area-inset-bottom))",
        paddingLeft: "max(1.5rem, env(safe-area-inset-left))",
      }}
    >
      <div className="flex items-center gap-3">
        <div
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg"
          style={{ background: "var(--color-accent-primary)", color: "#fff" }}
        >
          <HardHat className="h-7 w-7" aria-hidden />
        </div>
        <span className="text-xl font-semibold tracking-tight">Builder</span>
      </div>
      {children}
    </div>
  );
}

export default function PublicOfferPage() {
  /** Route `:token` — w linku base64url (bez `/`); odpowiada `offer_shares.public_token` po lookup. */
  const { token: tokenParam } = useParams<{ token: string }>();
  const publicToken = decodePublicTokenRouteParam(tokenParam);
  const [state, setState] = useState<PublicOfferState>({ kind: "loading" });
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);

  useEffect(() => {
    if (!publicToken?.trim()) {
      setState({ kind: "notFound" });
      return;
    }

    const ac = new AbortController();
    const run = async () => {
      const base = import.meta.env.VITE_SUPABASE_URL as string | undefined;
      const apikey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined;
      if (!base || !apikey) {
        setState({ kind: "error" });
        return;
      }

      const url = buildGetOfferShareFunctionUrl(base, publicToken);

      try {
        const res = await fetch(url, {
          signal: ac.signal,
          headers: {
            apikey,
            Authorization: `Bearer ${apikey}`,
          },
        });

        if (res.status === 404) {
          setState({ kind: "notFound" });
          return;
        }

        if (!res.ok) {
          setState({ kind: "error" });
          return;
        }

        const body = (await res.json()) as {
          payload_json?: unknown;
          accepted_at?: string | null;
          accepted_by_name?: string | null;
        };
        const parsed = parsePayloadJson(body?.payload_json);
        if (!parsed) {
          setState({ kind: "error" });
          return;
        }
        setState({
          kind: "loaded",
          payload: parsed,
          acceptedAt: typeof body.accepted_at === "string" ? body.accepted_at : null,
          acceptedByName: typeof body.accepted_by_name === "string" ? body.accepted_by_name : null,
        });
      } catch (e: unknown) {
        if (e && typeof e === "object" && "name" in e && (e as { name?: string }).name === "AbortError") {
          return;
        }
        setState({ kind: "error" });
      }
    };

    void run();
    return () => ac.abort();
  }, [publicToken]);

  const handlePublicOfferAccept = useCallback(
    async (acceptedByName: string): Promise<void> => {
      if (!publicToken?.trim() || state.kind !== "loaded" || state.acceptedAt) return;
      const base = import.meta.env.VITE_SUPABASE_URL as string | undefined;
      const apikey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined;
      if (!base || !apikey) {
        setAcceptError("Nie udało się zapisać akceptacji. Spróbuj ponownie.");
        return;
      }

      setAccepting(true);
      setAcceptError(null);
      try {
        const url = `${base.replace(/\/$/, "")}/functions/v1/get-offer-share`;
        const res = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            apikey,
            Authorization: `Bearer ${apikey}`,
          },
          body: JSON.stringify({
            public_token: publicToken.trim(),
            accepted_by_name: acceptedByName.trim(),
          }),
        });

        if (!res.ok) {
          throw new Error("accept_failed");
        }

        const body = (await res.json()) as { accepted_at?: string | null; accepted_by_name?: string | null };
        setState((prev) =>
          prev.kind === "loaded"
            ? {
                ...prev,
                acceptedAt: typeof body.accepted_at === "string" ? body.accepted_at : new Date().toISOString(),
                acceptedByName:
                  typeof body.accepted_by_name === "string" ? body.accepted_by_name : acceptedByName.trim(),
              }
            : prev,
        );

        const notifyUrl = `${base.replace(/\/$/, "")}/functions/v1/notify-offer-accepted`;
        void fetch(notifyUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            apikey,
            Authorization: `Bearer ${apikey}`,
          },
          body: JSON.stringify({
            public_token: publicToken.trim(),
            accepted_by_name: acceptedByName.trim(),
          }),
        }).catch(() => {
          /* powiadomienie e-mail nie blokuje akceptacji */
        });
      } catch {
        setAcceptError("Nie udało się zapisać akceptacji. Spróbuj ponownie.");
      } finally {
        setAccepting(false);
      }
    },
    [state, publicToken],
  );

  if (state.kind === "loading") {
    return (
      <div
        className="flex min-h-[100dvh] items-center justify-center"
        style={{
          background: "var(--color-bg-page)",
          paddingTop: "env(safe-area-inset-top)",
          paddingRight: "env(safe-area-inset-right)",
          paddingBottom: "env(safe-area-inset-bottom)",
          paddingLeft: "env(safe-area-inset-left)",
        }}
      >
        <Loader2 className="h-10 w-10 animate-spin text-primary" aria-label="Ładowanie" />
      </div>
    );
  }

  if (state.kind === "notFound" || state.kind === "error") {
    return (
      <UnavailableShell>
        <div className="space-y-2">
          <h1 className="text-lg font-semibold text-foreground">Oferta niedostępna lub wygasła</h1>
          <p className="text-sm text-muted-foreground">
            Link mógł zostać cofnięty, jest nieprawidłowy lub oferta nie jest już udostępniana.
          </p>
        </div>
        <p className="max-w-sm text-sm text-muted-foreground">
          Potrzebujesz oferty? Skontaktuj się bezpośrednio z wykonawcą, który przesłał Ci link.
        </p>
      </UnavailableShell>
    );
  }

  const { payload } = state;
  const showFreeBranding = payload.isPro !== true;
  let offerDate: Date;
  try {
    offerDate = new Date(payload.clientOffer.offerDate);
    if (Number.isNaN(offerDate.getTime())) offerDate = new Date();
  } catch {
    offerDate = new Date();
  }

  return (
    <div
      className="relative min-h-[100dvh] print:min-h-0"
      style={{
        background: "var(--color-bg-page)",
        color: "var(--color-text-primary)",
        paddingTop: "env(safe-area-inset-top)",
        paddingRight: "env(safe-area-inset-right)",
        paddingBottom: "env(safe-area-inset-bottom)",
        paddingLeft: "env(safe-area-inset-left)",
      }}
    >
      {showFreeBranding ? (
        <div className="pointer-events-auto fixed bottom-4 right-4 z-10 max-w-fit">
          <a
            href={LANDING_PAGE_HREF}
            className="inline-block rounded-full border border-border/60 bg-background/90 px-3 py-1.5 text-xs text-muted-foreground shadow-sm backdrop-blur-sm transition-colors hover:text-foreground"
          >
            Oferta w Builder
          </a>
        </div>
      ) : null}
      <div className="mx-auto max-w-[900px] px-4 py-6 sm:px-5 sm:py-8">
        <ClientOfferView
          lines={payload.lines}
          offerTexts={payload.offerTexts}
          offerAssumptions={payload.offerAssumptions}
          templateType={resolveTemplateType(payload.templateType)}
          offerDate={offerDate}
          draftId={payload.clientOffer.draftId}
          offerNumber={payload.clientOffer.offerNumber}
          clientName={payload.clientOffer.clientName}
          clientAddress={payload.clientOffer.clientAddress}
          clientPhone={payload.clientOffer.clientPhone}
          clientEmail={payload.clientOffer.clientEmail}
          companyName={payload.clientOffer.companyName}
          companyNip={payload.clientOffer.companyNip}
          companyAddress={payload.clientOffer.companyAddress}
          companyPhone={payload.clientOffer.companyPhone}
          companyEmail={payload.clientOffer.companyEmail}
          companyLogoUrl={payload.clientOffer.companyLogoUrl}
          companyLogoPath={payload.clientOffer.companyLogoPath}
          templateId={resolvePublicOfferTemplateId(payload.templateId)}
          materialsOwnership={payload.materialsOwnership}
          materialsNotes={payload.materialsNotes}
          paymentTerms={payload.paymentTerms}
          warrantyType={payload.warrantyType}
          exclusions={payload.exclusions}
          warrantyNotes={payload.warrantyNotes}
          isPublicOfferView
          publicOfferToken={publicToken?.trim() || undefined}
          publicOfferAcceptedAt={state.acceptedAt}
          publicOfferAcceptedByName={state.acceptedByName}
          publicOfferAccepting={accepting}
          publicOfferAcceptError={acceptError}
          onPublicOfferAccept={handlePublicOfferAccept}
          showBuilderBranding={showFreeBranding}
        />
      </div>
    </div>
  );
}
